import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, CalendarCheck } from "lucide-react";
import { CLINIC, getClinicStatus } from "../data/content";
import { scrollToId } from "../lib/scroll";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Smile Gallery", href: "#gallery" },
  { label: "The Specialist", href: "#doctor" },
  { label: "Hours", href: "#book" },
  { label: "Visit Us", href: "#visit" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const status = getClinicStatus();

  const go = (href) => {
    setOpen(false);
    scrollToId(href);
  };

  return (
    <header
      data-testid="site-navbar"
      className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-[#070B12]/80 backdrop-blur-2xl backdrop-saturate-150"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <button
          data-testid="nav-brand"
          onClick={() => go("#top")}
          className="flex items-center gap-3 text-left"
        >
          <span className="rounded-2xl bg-white p-1.5 shadow-[0_0_18px_rgba(23,210,245,0.25)] ring-1 ring-toothcyan/30">
            <img
              src={CLINIC.logo}
              alt="Narayan Dental Care logo"
              className="h-14 w-14 scale-110 rounded-xl object-contain"
            />
          </span>
          <span className="leading-tight">
            <span className="block font-serif text-xl font-semibold tracking-tight text-ivory">
              Narayan Dental Care
            </span>
            <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.28em] text-ember">
              Multi Speciality Clinic
            </span>
          </span>
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(l.href)}
              className="group relative text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-white"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 rounded-full bg-gradient-to-r from-toothcyan to-ember transition-all duration-300 group-hover:w-full" />
            </button>
          ))}
          <span
            data-testid="nav-open-status-pill"
            className="hidden items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-950/50 px-3 py-1.5 shadow-[0_0_15px_rgba(16,185,129,0.15)] xl:flex"
          >
            <span className="relative flex h-2 w-2">
              <span
                className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-60 ${
                  status.open ? "bg-emerald-400" : "bg-ember"
                }`}
              />
              <span
                className={`relative inline-flex h-2 w-2 rounded-full ${
                  status.open ? "bg-emerald-400" : "bg-ember"
                }`}
              />
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-emerald-300/90">
              {status.label}
            </span>
          </span>
          <button
            data-testid="nav-book-appointment-button"
            onClick={() => go("#book")}
            className="flex items-center gap-2 rounded-full bg-gradient-to-r from-toothcyan to-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition-all duration-300 hover:shadow-[0_0_22px_rgba(23,210,245,0.45)] active:scale-[0.97]"
          >
            <CalendarCheck className="h-4 w-4" />
            Book Appointment
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          onClick={() => setOpen(!open)}
          className="rounded-lg p-2 text-ivory lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-[#070B12] lg:hidden"
          >
            <div className="flex flex-col gap-1 px-5 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l.href)}
                  className="rounded-lg px-3 py-2.5 text-left text-sm font-medium text-slate-200 hover:bg-white/5"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-book-appointment-button"
                onClick={() => go("#book")}
                className="mt-2 flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-toothcyan to-blue-600 px-5 py-3 text-sm font-semibold text-white active:scale-[0.97]"
              >
                <CalendarCheck className="h-4 w-4" />
                Book Appointment
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
